/**
 * Main Application Module
 * Configures routing, state management, and core application providers
 */

import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import Layout from "./components/layout";
import ThemeProvider from "./context/theme-context";
import { Provider, useDispatch } from "react-redux";
import store from "./store/store";
import "./index.css";
import PageNotFound from "./components/page-not-found";
import Homepage from "./components/homepage";
import Collections from "./components/collections/collections";
import Claim from "./components/claim/claim";
import NFTGallery from "./components/nft-gallery/nft-gallery";
import Login from "./components/admin/login";
import { QueryClient, QueryClientProvider } from "react-query";
import { AuthProvider } from "./context/auth-provider";
import Dashboard from "./components/admin/dashboard";
import useAuth from "./hooks/use-auth";
import NewCollection from "./components/admin/new-collection";
import "./interceptors/auth-interceptor";
import AdminHome from "./components/admin/admin-home";
import { setConfig } from "./store/config/config-slice";
import axios from "axios";
import ClaimIds from "./components/admin/claim-ids";
import CollectionClaimId from "./components/admin/collection-claim-id";
import Settings from "./components/admin/settings/settings";
import CustomerConfigurations from "./components/admin/settings/customer-configurations";
import UserPermissions from "./components/admin/settings/user-permissions";
import ThemeShowcase from "./components/admin/theme-showcase";
import Report from "./components/report/report";
import Reports from "./components/admin/reports/reports";
import Roles from "./components/admin/settings/roles";
import UserRoles from "./components/admin/settings/user-roles";
import ClientPermissions from "./components/admin/settings/client-permissions";
import ManageUsers from "./components/admin/settings/manage-users";
import CollectionDetail from "./components/collections/collection-details";
import NFTDetail from "./components/nft-gallery/nft-details";
import SingleProduct from "./components/single-product";
import CollectionsMockup from "./components/collections-mockup";

import "./styles/app.scss";
/**
 * Query client instance for React Query
 * Manages and caches server state throughout the application
 */
const queryClient = new QueryClient();

/**
 * RequireAuth Component
 * Authentication guard component that protects routes requiring user authentication
 * Handles permission-based routing and redirects unauthorized access
 * @returns {JSX.Element} Protected route outlet or navigation to login
 */
const RequireAuth = () => {
  const { auth } = useAuth();
  const location = useLocation();

  if (!auth?.userInfo) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const hasPermission = auth?.userRole?.clientPermission?.some((permission) => {
    const pattern = permission.path.replace(":collection", "[^/]+");
    return new RegExp(`^${pattern}$`).test(location.pathname);
  });

  if (!hasPermission) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

/**
 * ConfigInitializer Component
 * Fetches and initializes application configuration on mount
 * Updates Redux store with customer configuration data
 * @returns {null} Component doesn't render any UI elements
 */
const ConfigInitializer = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const initializeConfig = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_SERVER_URL + "/api/customer-configurations",
        );



        if (response.data && response.data.data) {
          response.data.data.theming.currentTheme = "light";
        }

        dispatch(setConfig(response.data));
      } catch (error) {
        console.log(error);
      }
    };

    initializeConfig();
  }, [dispatch]);

  return null;
};

/**
 * App Component
 * Root component of the application that sets up providers and routing
 * Configures authentication, state management, and theme providers
 * @returns {JSX.Element} Configured application with routing structure
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Provider store={store}>
          <ConfigInitializer />
          <ThemeProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Layout />}>
                  {/* Redirect dalla home alle collections */}
                  <Route
                    index
                    element={<Navigate to="/collections" replace />}
                  />
                  <Route path="/collections" element={<Collections />} />
                  <Route
                    path="/collection/:contractAddress"
                    element={<CollectionDetail />}
                  />
                  <Route
                    path="/claim/:collection/:claimId"
                    element={<Claim />}
                  />
                  <Route
                    path="/report/:collection/:claimId"
                    element={<Report />}
                  />
                  <Route path="/nft-gallery" element={<NFTGallery />} />
                  <Route
                    path="/nft/:contractAddress/:tokenId"
                    element={<NFTDetail />}
                  />
                  <Route path="/privacy-policy" />
                  <Route path="/terms" />
                  <Route path="/contact" />
                </Route>
                <Route path="/login" element={<Login />} />
                <Route element={<RequireAuth />}>
                  <Route path="/admin" element={<Dashboard />}>
                    <Route index element={<AdminHome />} />
                    <Route path="new-collection" element={<NewCollection />} />
                    <Route path="claim-ids" element={<ClaimIds />} />
                    <Route path="reports" element={<Reports />} />
                    <Route
                      path="claim-ids/:collection"
                      element={<CollectionClaimId />}
                    />
                    <Route path="settings" element={<Settings />}>
                      <Route
                        path="configurations"
                        element={<CustomerConfigurations />}
                      />
                      <Route
                        path="theme-showcase"
                        element={<ThemeShowcase />}
                      />
                      <Route
                        path="user-permissions"
                        element={<UserPermissions />}
                      />
                      <Route
                        path="client-permissions"
                        element={<ClientPermissions />}
                      />
                      <Route path="manage-users" element={<ManageUsers />} />
                      <Route path="roles" element={<Roles />} />
                      <Route path="user-roles" element={<UserRoles />} />
                    </Route>
                  </Route>
                </Route>

                <Route path="/not-found" element={<PageNotFound />} />
                <Route path="*" element={<Navigate to="/not-found" />} />
              </Routes>
            </BrowserRouter>
          </ThemeProvider>
        </Provider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
